import AppContext from 'App/AppContext';
import { IPFontIcon, IPScrollablePane } from 'baseComponents';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import AuthSchemaPicker from 'components/Pickers/AuthSchemaPicker';
import {
  globalStackItemStylesPaddingSceneScroll,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
} from 'globalStyles';
import Tag from 'models/tag';
import Task from 'models/tasks/task';
import { Fragment, useContext } from 'react';
import { TaskDetailAssignment } from './Details/TaskDetailAssignment';
import { TaskDetailDeadline } from './Details/TaskDetailDeadline';
import { TaskDetailDescription } from './Details/TaskDetailDecription';
import { TaskDetailExternalId } from './Details/TaskDetailExternalId';
import { TaskDetailName } from './Details/TaskDetailName';
import { TaskDetailStart } from './Details/TaskDetailStart';
import { TaskDetailStatus } from './Details/TaskDetailStatus';
import { iconFontSize } from './Details/TaskDetailStyles';
import { TaskDetailTags } from './Details/TaskDetailTags';
import { EventSubTasks } from './EventSubTasks';

interface IEventTaskDetail {
  task: Task;
  orgTask: Task;
  isActionPending: boolean;
  tags: Tag[];
  windowLevel?: number;
  navigateExternal?: boolean;
  canUpdate: boolean;
  // the name is derived from something else (an audit event is named after its audit), so editing it
  // here would only create a mismatch
  nameReadonly?: boolean;
  getErrorMessage: (field: string) => string | undefined;
  onUpdate: (newTask: Task) => void;
  addTagToTaskState: (tag: Tag) => void;
  removeTagFromTaskState: (tag: Tag) => void;
  onChangeCompletionDate: (completionDate: Date) => void;
}

export const EventTaskDetail = (props: IEventTaskDetail) => {
  const appContext = useContext(AppContext);

  //
  // Main render
  //
  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPStack verticalFill tokens={globalStackTokensGapSmall} styles={{ root: { paddingRight: 10 } }}>
            <TaskDetailName
              orgTask={props.orgTask}
              task={props.task}
              canUpdate={props.canUpdate}
              readonly={props.nameReadonly}
              onUpdate={props.onUpdate}
              getErrorMessage={props.getErrorMessage}
            />
            <TaskDetailTags
              orgTask={props.orgTask}
              task={props.task}
              tags={props.tags}
              canUpdate={props.canUpdate}
              addTagToTaskState={props.addTagToTaskState}
              removeTagFromTaskState={props.removeTagFromTaskState}
              isActionPending={props.isActionPending}
            />
            <TaskDetailDescription
              orgTask={props.orgTask}
              task={props.task}
              canUpdate={props.canUpdate}
              onUpdate={props.onUpdate}
            />
            {props.task.isSeries() && (
              <Fragment>
                <IPStack
                  horizontal
                  tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
                  verticalAlign="start"
                >
                  <IPStack.Item>
                    {!appContext.isMobileView && <IPFontIcon iconName="FavoriteStarFill" style={iconFontSize} />}
                  </IPStack.Item>
                  <IPStack.Item>
                    <AuthSchemaPicker
                      selectedAuthSchemaId={props.task.authSchemaId}
                      onSelect={(schema) => {
                        const copyTask = props.task.clone();
                        copyTask.authSchemaId = schema?.authSchemaId;
                        props.onUpdate(copyTask);
                      }}
                    />
                  </IPStack.Item>
                  <TaskDetailStatus
                    task={props.task}
                    orgTask={props.orgTask}
                    canUpdate={props.canUpdate}
                    onUpdate={props.onUpdate}
                    onChangeCompletionDate={props.onChangeCompletionDate}
                    showCompleted={props.task.taskId > 0}
                    controlsOnly={true}
                  />
                </IPStack>
                <TaskDetailStart
                  orgTask={props.orgTask}
                  task={props.task}
                  canUpdate={props.canUpdate}
                  onUpdate={props.onUpdate}
                  getErrorMessage={props.getErrorMessage}
                  hideEndTime={true}
                />
                <EventSubTasks
                  task={props.task}
                  isActionPending={props.isActionPending}
                  windowLevel={props.windowLevel}
                  navigateExternal={props.navigateExternal}
                  onUpdate={props.onUpdate}
                />
              </Fragment>
            )}
            {!props.task.isSeries() && (
              <Fragment>
                <TaskDetailAssignment
                  orgTask={props.orgTask}
                  task={props.task}
                  canUpdate={props.canUpdate}
                  onUpdate={props.onUpdate}
                  getErrorMessage={props.getErrorMessage}
                />
                <TaskDetailStatus
                  task={props.task}
                  orgTask={props.orgTask}
                  canUpdate={props.canUpdate}
                  onUpdate={props.onUpdate}
                  onChangeCompletionDate={props.onChangeCompletionDate}
                  showCompleted={props.task.taskId > 0}
                />
                <TaskDetailStart
                  orgTask={props.orgTask}
                  task={props.task}
                  canUpdate={props.canUpdate}
                  onUpdate={props.onUpdate}
                  getErrorMessage={props.getErrorMessage}
                />
                <TaskDetailDeadline
                  orgTask={props.orgTask}
                  task={props.task}
                  canUpdate={props.canUpdate}
                  onUpdate={props.onUpdate}
                  getErrorMessage={props.getErrorMessage}
                />
              </Fragment>
            )}
            <TaskDetailExternalId
              orgTask={props.orgTask}
              task={props.task}
              canUpdate={props.canUpdate}
              onUpdate={props.onUpdate}
            />
          </IPStack>
        </IPScrollablePane>
      </IPStack.Item>
    </IPStack>
  );
};
